import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaPrint } from 'react-icons/fa';

import Loading from '../../components/Loading';
import axios from '../../services/axios';
import {
   getMovement,
   readMovementApiError,
} from '../../services/movements/movementsApi';
import {
   formatMovementDateTime,
   movementFlagLabel,
   movementPdvLabel,
   movementUserLabel,
} from '../../utils/movementFormat';
import { notifyApiError } from '../../utils/feedback';

import { Container } from '../../styles/layout/Container';
import { Section, SectionBody } from '../../styles/layout/Section';
import { EmptyState } from '../../styles/feedback/EmptyState';
import { CtaOnDarkButton, GhostOnDarkButton } from '../../styles/ui/Button';

import ReceiptDocument from './Document';
import {
   HALF_SHEET_MAX_ITEMS,
   PAGE,
   PreviewBar,
   PrintPageStyle,
} from './styled';

// ===========================================================================
// Comprovante do movimento — pré-visualização E documento, em rota própria
// (`/movements/:id/receipt`).
//
// **Um papel só: A4 retrato** (decisão do Caio, 2026-09-13, simplificando o
// contrato original de dois formatos). O que varia é a ÁREA ocupada:
//
//   até 5 itens → meia folha + linha "Recorte aqui" no meio: o operador corta
//                 e guarda a metade limpa para a próxima impressão;
//   6+ itens    → folha inteira, com o campo de observações da conferência.
//
// Sem seletor de formato: não há formato a escolher, e a área é consequência
// do conteúdo. A legenda da barra diz qual área saiu e por quê — a
// transparência que o handoff pede, agora com uma informação útil a mais (que
// a folha pode ser cortada).
//
// Por que rota e não modal: `@page` (tamanho e margem do papel) é regra de
// DOCUMENTO, não de elemento. Dentro de uma modal, o tamanho de papel valeria
// para o app inteiro. Na rota, o `PrintPageStyle` monta com a página e
// desmonta com ela. De brinde: URL compartilhável, nada de modal-sobre-modal
// (débito FC7 §H.2.2) e a barra de ferramentas sai do papel por
// `data-print-hide`.
//
// A impressão é do NAVEGADOR (`window.print()`): sem Puppeteer, sem
// dependência nova e com o layout vivendo ao lado da tela que ele espelha.
// O que o front não controla: o cabeçalho/rodapé do próprio navegador (URL,
// data) — preferência do diálogo, desmarcada uma vez pelo operador.
//
// Gate: a ROTA exige `inventory:movement:print` (routes/index.js), então esta
// tela não re-checa permissão.
// ===========================================================================

function formatNow() {
   return new Date().toLocaleString('pt-BR');
}

export default function MovementReceipt() {
   const { id } = useParams();
   const navigate = useNavigate();
   const location = useLocation();

   const authUser = useSelector((state) => state.auth.user);

   const [movement, setMovement] = useState(null);
   const [productDetails, setProductDetails] = useState([]);
   const [isLoading, setIsLoading] = useState(true);
   const [loadFailed, setLoadFailed] = useState(false);

   // Carimbo de emissão: hora da IMPRESSÃO, não do movimento (o contrato
   // frisa que os dois aparecem no documento e não devem ser confundidos).
   const [issuedAt, setIssuedAt] = useState(formatNow);
   const [printQueued, setPrintQueued] = useState(false);

   useEffect(() => {
      let alive = true;

      const load = async () => {
         setIsLoading(true);
         try {
            const data = await getMovement(id);
            if (!alive) return;
            setMovement(data);
            setLoadFailed(false);

            // Mesmo N+1 do detalhe e do editor: um GET /products/:id por
            // item, pareado por índice. Não há endpoint que devolva o item
            // já com SKU e nome.
            if (data?.ItemMovements?.length) {
               const responses = await Promise.all(
                  data.ItemMovements.map((item) =>
                     axios.get(`/products/${item.prod_id}`)
                  )
               );
               if (!alive) return;
               setProductDetails(
                  responses.map((res) => res.data.data?.product ?? res.data)
               );
            } else {
               setProductDetails([]);
            }
         } catch (error) {
            if (!alive) return;
            setMovement(null);
            setProductDetails([]);
            setLoadFailed(true);
            notifyApiError(error, {
               fallback: readMovementApiError(error).message,
            });
         } finally {
            if (alive) setIsLoading(false);
         }
      };

      load();
      return () => {
         alive = false;
      };
   }, [id]);

   const items = useMemo(() => {
      const rows = movement?.ItemMovements ?? [];
      return rows.map((item, index) => {
         const product = productDetails[index];
         return {
            key: String(item?.id ?? `${item?.prod_id}-${index}`),
            sku: String(product?.sku ?? product?.prod_sku ?? item?.prod_id ?? ''),
            name:
               product?.name ??
               product?.prod_name ??
               `Produto ${item?.prod_id}`,
            // O cadastro de produto não tem unidade de medida no front; o
            // próprio contrato do design define 'UN' como default.
            unit: 'UN',
            qty: Number(item?.prod_qtd) || 0,
         };
      });
   }, [movement, productDetails]);

   const totalUnits = useMemo(
      () => items.reduce((total, item) => total + item.qty, 0),
      [items]
   );

   const flag = String(movement?.movement_flag ?? '')
      .trim()
      .toLowerCase();
   const isVoid = flag === 'deleted' || flag === 'excluded';

   // Área do documento: consequência do nº de itens, sem override manual.
   const isFullSheet = items.length > HALF_SHEET_MAX_ITEMS;

   const areaLegend = isFullSheet
      ? `${PAGE.label} · folha inteira (${items.length} itens) · com campo de observações`
      : `${PAGE.label} · meia folha (até ${HALF_SHEET_MAX_ITEMS} itens) · corte na linha e reaproveite a metade limpa`;

   // Re-carimba a emissão e só então imprime: o efeito roda DEPOIS do commit,
   // então o papel sai com a hora do clique, não com a hora em que a
   // pré-visualização foi aberta.
   const handlePrint = useCallback(() => {
      setIssuedAt(formatNow());
      setPrintQueued(true);
   }, []);

   useEffect(() => {
      if (!printQueued) return;
      setPrintQueued(false);
      window.print();
   }, [printQueued]);

   const handleBack = () => {
      if (location.key !== 'default') navigate(-1);
      else navigate(`/movements/${id}`);
   };

   const cancellation = isVoid
      ? {
           reason: movement?.cancelled_reason ?? '',
           at: formatMovementDateTime(movement?.cancelled_at),
           by:
              [movement?.cancelled_by, movement?.cancelled_by_name]
                 .filter(Boolean)
                 .join(' - ') || '',
        }
      : null;

   const issuedBy = authUser
      ? [authUser.id, authUser.nome ?? authUser.name].filter(Boolean).join(' - ')
      : '—';

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PreviewBar data-print-hide>
            <div className="meta">
               <h1>Comprovante do movimento #{movement?.id ?? id}</h1>
               <p>{areaLegend}</p>
            </div>

            <div className="acts">
               <GhostOnDarkButton type="button" onClick={handleBack}>
                  Fechar
               </GhostOnDarkButton>

               <CtaOnDarkButton
                  type="button"
                  onClick={handlePrint}
                  disabled={isLoading || !movement}
               >
                  <FaPrint size={11} aria-hidden="true" /> Imprimir
               </CtaOnDarkButton>
            </div>
         </PreviewBar>

         {!movement && !isLoading ? (
            <Section data-print-hide>
               <SectionBody>
                  <EmptyState
                     title={
                        loadFailed
                           ? 'Não foi possível abrir o comprovante'
                           : 'Movimento não encontrado'
                     }
                     description="Verifique o ID e tente novamente."
                     action={
                        <GhostOnDarkButton type="button" onClick={handleBack}>
                           Voltar
                        </GhostOnDarkButton>
                     }
                  />
               </SectionBody>
            </Section>
         ) : (
            movement && (
               <>
                  <PrintPageStyle />
                  <ReceiptDocument
                     full={isFullSheet}
                     movementId={movement.id ?? id}
                     statusLabel={movementFlagLabel(movement.movement_flag)}
                     isVoid={isVoid}
                     moveType={movement.move_type}
                     transferRole={movement.transfer?.role ?? null}
                     pdvLabel={movementPdvLabel(movement)}
                     userLabel={movementUserLabel(movement)}
                     docType={movement.doc_type}
                     docNumber={movement.doc_number}
                     dateTime={formatMovementDateTime(movement.created_at)}
                     description={movement.movement_description}
                     items={items}
                     totalUnits={totalUnits}
                     cancellation={cancellation}
                     issuedBy={issuedBy}
                     issuedAt={issuedAt}
                  />
               </>
            )
         )}
      </Container>
   );
}
